import { useState, useEffect, useRef } from 'react'
import { Bell, X, Tag, CheckCheck, Home, Calendar, FileText, CreditCard, AlertTriangle } from 'lucide-react'
import { useT } from '../i18n/index.js'

// ── Types ──

export type NotifType = 'offer' | 'visit' | 'document' | 'payment' | 'alert'

export interface Notification {
  id: string
  type: NotifType
  title: string
  body: string
  read: boolean
  createdAt: string
}

const TYPE_ICONS: Record<NotifType, React.ElementType> = {
  offer: Tag,
  visit: Calendar,
  document: FileText,
  payment: CreditCard,
  alert: AlertTriangle,
}

const TYPE_COLORS: Record<NotifType, string> = {
  offer: 'var(--color-primary)',
  visit: '#059669',
  document: '#D97706',
  payment: '#7C3AED',
  alert: '#DC2626',
}

// ── LocalStorage fallback ──

const LS_KEY = 'patrimia.notifications'

function loadNotifs(): Notification[] {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return JSON.parse(raw) as Notification[]
  } catch { /* ignore */ }
  return []
}

function saveNotifs(notifs: Notification[]): void {
  try { localStorage.setItem(LS_KEY, JSON.stringify(notifs)) } catch { /* ignore */ }
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60_000)
  if (min < 1) return "à l'instant"
  if (min < 60) return `il y a ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `il y a ${h}h`
  const d = Math.floor(h / 24)
  return `il y a ${d}j`
}

// ── Component ──

export function NotificationPanel() {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [notifs, setNotifs] = useState<Notification[]>(loadNotifs)
  const panelRef = useRef<HTMLDivElement>(null)

  // Fetch from API (fire-and-forget on mount)
  useEffect(() => {
    fetch('/api/notifications')
      .then(r => r.json())
      .then((data: Notification[]) => {
        if (Array.isArray(data) && data.length > 0) {
          setNotifs(data)
          saveNotifs(data)
        }
      })
      .catch(() => { /* use localStorage fallback */ })
  }, [])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Escape key
  useEffect(() => {
    if (!open) return
    function handler(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  const unread = notifs.filter(n => !n.read).length

  function markAllRead() {
    const updated = notifs.map(n => ({ ...n, read: true }))
    setNotifs(updated)
    saveNotifs(updated)
  }

  function markRead(id: string) {
    const updated = notifs.map(n => n.id === id ? { ...n, read: true } : n)
    setNotifs(updated)
    saveNotifs(updated)
  }

  function resolveLabel(key: string): string {
    const parts = key.split('.')
    let val: unknown = t
    for (const p of parts) {
      if (val && typeof val === 'object' && p in (val as Record<string, unknown>)) {
        val = (val as Record<string, unknown>)[p]
      } else return key
    }
    return typeof val === 'string' ? val : key
  }

  // Sort: unread first
  const sorted = [...notifs].sort((a, b) => {
    if (!a.read && b.read) return -1
    if (a.read && !b.read) return 1
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })

  return (
    <div ref={panelRef} className="relative">
      {/* Bell button */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="relative min-h-[44px] min-w-[44px] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-1"
        aria-label={resolveLabel('notifications.label')}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            className="absolute top-2 right-2 h-4 min-w-[16px] px-0.5 rounded-full bg-[var(--color-danger,#DC2626)] text-white text-[9px] font-bold flex items-center justify-center"
            aria-label={`${unread} ${resolveLabel('notifications.unread')}`}
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] shadow-2xl z-50"
          role="dialog"
          aria-modal="true"
          aria-label={resolveLabel('notifications.panelLabel')}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border)]">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-[var(--color-primary)]" />
              <h2 className="text-sm font-semibold text-[var(--color-text)]">
                {resolveLabel('notifications.title')}
              </h2>
              {unread > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-[var(--color-primary-light)] text-[var(--color-primary)] text-xs font-medium">
                  {unread}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="flex items-center gap-1 px-2 py-1 min-h-[36px] rounded text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
                  title={resolveLabel('notifications.markAllRead')}
                >
                  <CheckCheck size={14} />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="min-h-[36px] min-w-[36px] flex items-center justify-center text-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]"
                aria-label={t.common.close}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto" role="list">
            {sorted.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[var(--color-text-secondary)]">
                {resolveLabel('notifications.empty')}
              </div>
            )}
            {sorted.map(n => {
              const Icon = TYPE_ICONS[n.type] ?? Bell
              return (
                <div
                  key={n.id}
                  role="listitem"
                  className={`flex items-start gap-3 px-4 py-3 border-b border-[var(--color-border-subtle)] last:border-0 cursor-pointer transition-colors ${
                    !n.read ? 'bg-[var(--color-primary-light)]/30' : 'hover:bg-[var(--color-surface-raised)]'
                  }`}
                  onClick={() => markRead(n.id)}
                >
                  <div
                    className="mt-0.5 h-8 w-8 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${TYPE_COLORS[n.type]}1a` }}
                  >
                    <Icon size={15} style={{ color: TYPE_COLORS[n.type] }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm leading-snug ${!n.read ? 'font-semibold text-[var(--color-text)]' : 'font-medium text-[var(--color-text)]'}`}>
                        {n.title}
                      </p>
                      {!n.read && (
                        <span className="mt-1 h-2 w-2 rounded-full bg-[var(--color-primary)] shrink-0" aria-label={resolveLabel('notifications.unread')} />
                      )}
                    </div>
                    <p className="text-xs text-[var(--color-text-secondary)] mt-0.5 leading-snug">{n.body}</p>
                    <p className="text-[10px] text-[var(--color-text-disabled)] mt-1">{relativeTime(n.createdAt)}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
